import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from '@angular/router';
import {
  FUNCTION_RESULT_STATUS,
  IObjectKey
} from "@nova/common-interfaces";
import { IServicePlan, ISubscription } from "@rfm/shared-interfaces";
import { AutoComplete } from 'primeng/autocomplete';
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { DatePickerModule } from "primeng/datepicker";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { CustomerService } from "../../../../shared/services/customers/customers.service.mjs";
import { ServicePlanService } from "../../../../shared/services/products/service-plan.service.mjs";
import { SubscriptionService } from "../../../../shared/services/products/subscription.service.mjs";

@Component({
  selector: "app-subscription-view",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    AutoComplete,
    ProgressSpinnerModule,
  ],
  templateUrl: "./subscription-edit.component.html",
  styleUrls: ["./subscription-edit.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SubscriptionEditComponent implements OnInit {
  subscriptionForm!: FormGroup;
  selectedPlan: IServicePlan | undefined;
  selectedCustomer: IObjectKey | undefined;
  activationDate: Date | undefined;
  nextDue: Date | undefined;
  name: string | undefined;
  months: number| undefined;
  actualAmount: number | undefined;

  customers: IObjectKey[] = [];
  servicePlans: IServicePlan[] = [];
  filteredCustomers: IObjectKey[] = [];
  filteredServicePlans: IServicePlan[] = [];

  subscription: ISubscription | undefined;

  isLoading = true;

  searchCustomers(event: any)
  {
    this.filteredCustomers = this.customers.filter(x => x.name?.toLowerCase().includes((event.query as string).toLowerCase()));
  }

  searchServicePlans(event: any)
  {
    this.filteredServicePlans = this.servicePlans.filter(x => x.name?.toLowerCase().includes((event.query as string).toLowerCase()));
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private customerService: CustomerService,
    private servicePlanService: ServicePlanService
  ) {}

  ngOnInit(): void {
    this.subscriptionForm = this.fb.group({
      customer: ["", Validators.required],
      plan: ["", Validators.required],
    });

    const promises: Promise<any>[] = [];

    const id = this.activatedRoute.snapshot.params["id"]

    promises.push(this.customerService.getKeys().then(async (result: any) => {
      if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
        console.error("Unable to get customers");
        return Promise.reject("Unable to get customers");
      }

      this.customers = result.data!;
      this.filteredCustomers = this.customers;
    }));

    promises.push(this.servicePlanService.getAll().then(async (result: any) => {
      if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
        console.error("Unable to get service plans");
        return Promise.reject("Unable to get service plans");
      }

      this.servicePlans = result.data!;
      this.filteredServicePlans = this.servicePlans;
    }));

    Promise.all(promises).then(() => {
      this.subscriptionService.get(id).then(async (result) => {
        if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
          console.error("Unable to get subscriptions");
          return Promise.reject("Unable to get subscriptions");
        }

        this.subscription = result.data!;
  
        const customerId = result.data?.customer_id.id;
        const planId = result.data?.plan?.id;
  
        this.selectedCustomer = this.customers.find(c => c.id === customerId);
        this.selectedPlan = this.servicePlans.find(s => s.id === planId);
        this.activationDate = new Date(result.data?.activation_date!);
        this.nextDue = new Date(result.data?.next_due!);
        this.name = result.data?.name;
        this.months = result.data?.months_to_bill
        this.actualAmount = result.data?.actual_amount;
      })
      .finally(() => {
        this.isLoading = false;
      });
    })
  }

  onSubmit(): void {
    if (
      this.selectedCustomer &&
      this.selectedPlan
    ) {
      this.isLoading = true;

      this.subscription!.activation_date = this.activationDate?.getTime()!;
      this.subscription!.next_due = this.nextDue?.getTime()!;
      this.subscription!.plan = this.selectedPlan;
      this.subscription!.customer_id = this.selectedCustomer;
      this.subscription!.name = this.name;
      this.subscription!.months_to_bill = this.months!;
      this.subscription!.actual_amount = this.actualAmount!;

      this.subscriptionService
        .update(this.subscription!)
        .then(() => {
          console.log("Subscription updated successfully");
          this.router.navigate(["/admin/subscriptions"]);
        })
        .catch((error: any) => {
          console.error("Error updating subscription:", error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
}
